import React, { useEffect, useState } from 'react';
import Toolbox from '../components/toolbox/Toolbox';
import Canvas from '../components/canvas/Canvas';
import Canvas3D from '../components/canvas/Canvas3D';
import Toolbar from '../components/common/Toolbar';
import RightPanel from '../components/common/RightPanel';
import CanvasFooter from '../components/common/CanvasFooter';
import DxfImportOverlay from '../components/common/DxfImportOverlay';
import FloatingCanvasMenu from '../components/common/FloatingCanvasMenu';
import EquipmentModal from '../components/equipment/EquipmentModal';
import AssistantChatWidget from '../components/chat/AssistantChatWidget';
import { MindmapHighlightBanner } from '../components/canvas/MindmapHighlightOverlay';
import ProcessMachineMindmap from '../components/mindmap/ProcessMachineMindmap';
import LayoutChangeConfirmationModal from '../components/common/LayoutChangeConfirmationModal';
import { useAppDispatch, useAppSelector } from '../hooks/redux';
import {
  setSimulationPanelOpen,
  setWorkspaceView,
  resetRouteEndpoints,
} from '../features/ui/uiSlice';
import SimulationPanel from '../components/simulation/SimulationPanel';
import SimulationErrorsPanel from '../components/simulation/SimulationErrorsPanel';
import SimulationLegend from '../components/simulation/SimulationLegend';
import { stopSimulation } from '../features/simulation/simulationSlice';
import { useKeyboardShortcuts } from '../hooks/useKeyboardShortcuts';
import { useAutosave } from '../hooks/useAutosave';
import { useEquipmentShortcuts } from '../hooks/useEquipmentShortcuts';
import { useDxfOverlayRegeneration } from '../hooks/useDxfOverlayRegeneration';
import SimulationRuntimeDrivers from '../components/simulation/SimulationRuntimeDrivers';
import { clearLegacyEquipmentStorage } from '../utils/equipmentStorage';
import { useLocation } from 'react-router-dom';
import {
  Canvas3DLoadingOverlay,
  type Scene3DLoadingStage,
} from '../components/canvas/Canvas3DLoadingOverlay';

const EditorLayout: React.FC = () => {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const simulationMode = useAppSelector((state) => state.ui.simulationPanelOpen);
  const view3dOpen = useAppSelector((state) => state.ui.view3dOpen);
  const view3dSceneRevealed = useAppSelector((state) => state.ui.view3dSceneRevealed);
  const workspaceView = useAppSelector((state) => state.ui.workspaceView);
  const showMindmap = workspaceView === 'mindmap';
  const showMapChrome = !view3dOpen;

  /** Defer mounting the heavy R3F tree so the loader can paint first. */
  const [mountCanvas3d, setMountCanvas3d] = useState(false);
  const [loaderStage, setLoaderStage] = useState<Scene3DLoadingStage>('opening');
  const [loaderProgress, setLoaderProgress] = useState<number | null>(null);

  useEffect(() => {
    if (!view3dOpen) {
      setMountCanvas3d(false);
      setLoaderStage('opening');
      setLoaderProgress(null);
      return;
    }

    setLoaderStage('opening');
    setLoaderProgress(null);
    setMountCanvas3d(false);

    let rafInner = 0;
    const rafOuter = requestAnimationFrame(() => {
      rafInner = requestAnimationFrame(() => setMountCanvas3d(true));
    });

    return () => {
      cancelAnimationFrame(rafOuter);
      cancelAnimationFrame(rafInner);
    };
  }, [view3dOpen]);

  const showView3dLoader = view3dOpen && !view3dSceneRevealed;
  const hide2dCanvas = view3dOpen && view3dSceneRevealed;

  // A fresh app/editor entry always opens the Editor tab, even if Simulator
  // was selected before navigation, reload, session restoration, or HMR.
  useEffect(() => {
    dispatch(stopSimulation());
    dispatch(setSimulationPanelOpen(false));
  }, [dispatch]);

  useEffect(() => {
    if (location.pathname === '/mindmap') {
      dispatch(setWorkspaceView('mindmap'));
    }
  }, [location.pathname, dispatch]);

  // Clear any leftover route start/end capture markers when (re)entering the editor, so they never
  // linger across navigation. The persistent start/end nodes live in the route itself.
  useEffect(() => {
    dispatch(resetRouteEndpoints());
  }, [dispatch]);

  // Initialize keyboard shortcuts and autosave
  useKeyboardShortcuts();
  useAutosave();
  useEquipmentShortcuts();
  // Rebuild DXF-derived overlays whenever layer visibility changes so hidden
  // layers contribute nothing to saves or the simulation payload.
  useDxfOverlayRegeneration();
  // The playback clock and rail-transfer animation hooks live in
  // <SimulationRuntimeDrivers /> below. They re-render on every live-stream
  // batch, and hosting them here re-rendered the entire editor tree with them.

  // Equipment overlays are session-only; drop any legacy localStorage cache.
  useEffect(() => {
    clearLegacyEquipmentStorage();
  }, []);

  // Always force light mode — remove `dark` class in case it was previously set.
  useEffect(() => {
    document.documentElement.classList.remove('dark');
  }, []);

  return (
    <div className="flex h-screen w-screen flex-col overflow-hidden bg-gray-50 dark:bg-gray-950">
      <SimulationRuntimeDrivers />
      <Toolbar />

      <div className="relative flex-1 overflow-hidden bg-white" style={{ backgroundColor: '#ffffff' }}>
        <main className="absolute inset-0 overflow-hidden">
          {/* Keep 2D visible under the loader until the 3D scene has warmed up. */}
          <div className={hide2dCanvas ? 'hidden' : 'absolute inset-0'} aria-hidden={hide2dCanvas}>
            <Canvas />
          </div>

          {mountCanvas3d && view3dOpen && (
            <Canvas3D
              renderLoadingOverlay={false}
              onLoadingStatusChange={(stage, progress) => {
                setLoaderStage(stage);
                setLoaderProgress(progress);
              }}
            />
          )}

          {showView3dLoader && (
            <Canvas3DLoadingOverlay stage={loaderStage} progress={loaderProgress} />
          )}
        </main>

        {/*
          The Simulation panel stays mounted while the Editor tab is shown.
          It hosts the live-stream socket; unmounting it on every tab switch
          closed the socket and reset the local stream state while the API job
          kept running, which read as "Stop did not work" when switching back.
        */}
        <div className={simulationMode ? 'contents' : 'hidden'} aria-hidden={!simulationMode}>
          {simulationMode && <SimulationLegend />}
          {simulationMode && <SimulationErrorsPanel />}
          <SimulationPanel />
        </div>
        {simulationMode ? null : showMindmap ? (
          <>
            {showMapChrome && <Toolbox />}
            <ProcessMachineMindmap />
          </>
        ) : (
          <>
            {showMapChrome && <Toolbox />}
            <RightPanel />
          </>
        )}

        <FloatingCanvasMenu />
        {showMapChrome && <MindmapHighlightBanner />}
        {showMapChrome && <AssistantChatWidget />}
        <DxfImportOverlay />
      </div>

      <CanvasFooter />

      <EquipmentModal />
      <LayoutChangeConfirmationModal />
    </div>
  );
};

export default EditorLayout;
